import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { walletAPI } from '../api/client';

export const AddMoneyPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentData, setPaymentData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('pending');
  const [isPolling, setIsPolling] = useState(false);
  const [commissionRate, setCommissionRate] = useState(20); // Default 20%

  // Fetch user's commission rate on mount
  useEffect(() => {
    const fetchCommission = async () => {
      try {
        const response = await walletAPI.getCommission();
        if (response.data.data?.load_in_commission) {
          setCommissionRate(response.data.data.load_in_commission);
        }
      } catch (err) {
        console.log('Using default commission rate');
      }
    };
    fetchCommission();
  }, []);

  const handleAddMoney = async (e) => {
    e.preventDefault();
    setError('');
    
    const parsedAmount = parseFloat(amount);
    
    // Validate minimum amount (BondPay requirement)
    if (parsedAmount < 200) {
      setError('Minimum amount required is ₹200. Please enter a higher amount.');
      return;
    }
    
    setLoading(true);

    try {
      const response = await walletAPI.addMoney(parsedAmount);
      setPaymentData(response.data.data);
      setPaymentStatus('pending');
      setIsPolling(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error adding money');
      setLoading(false);
    }
  };

  // Poll for payment status
  useEffect(() => {
    if (!isPolling || !paymentData) return;

    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await walletAPI.getPaymentStatus(paymentData.orderNo);
        const status = statusResponse.data.data.status;

        if (status === 'success') {
          setPaymentStatus('success');
          setIsPolling(false);
          clearInterval(pollInterval);
        } else if (status === 'failed') {
          setPaymentStatus('failed');
          setIsPolling(false);
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error('Error checking payment status:', err);
      }
    }, 2000); // Poll every 2 seconds

    return () => clearInterval(pollInterval);
  }, [isPolling, paymentData]);

  // Success state - show congratulations message
  if (paymentStatus === 'success' && paymentData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-blue-600">PaySolutions</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-800"
            >
              Back
            </button>
          </div>
        </nav>

        <div className="max-w-md mx-auto py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="mb-6">
              <div className="inline-block">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-green-600 mb-2">Congratulations!</h2>
            <p className="text-lg text-gray-700 mb-6">Your payment has been received ✅</p>

            <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your Order ID</p>
              <p className="text-2xl font-bold text-green-600 font-mono break-all">
                {paymentData.orderNo}
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Please note down this order ID for your records
              </p>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 text-left">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Amount Paid:</strong> ₹{paymentData.grossAmount}
              </p>
              <p className="text-sm text-gray-700 mb-2">
                <strong>Commission:</strong> ₹{paymentData.commission}
              </p>
              <p className="text-sm font-semibold text-green-600">
                <strong>Wallet Credited:</strong> ₹{paymentData.walletWillReceive}
              </p>
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition"
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment failed/expired state
  if (paymentStatus === 'failed' && paymentData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-blue-600">PaySolutions</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-800"
            >
              Back
            </button>
          </div>
        </nav>

        <div className="max-w-md mx-auto py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="text-5xl mb-4">❌</div>
            <h2 className="text-2xl font-bold text-red-600 mb-2">Payment Failed or Expired</h2>
            <p className="text-gray-600 mb-6">Your payment was not completed. No money was deducted from your wallet.</p>

            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-2">Order ID</p>
              <p className="text-sm font-mono font-bold text-gray-800 break-all">{paymentData.orderNo}</p>
            </div>

            <div className="text-left text-sm text-gray-600 mb-6 space-y-2">
              <p><strong>Amount Attempted:</strong> ₹{paymentData.grossAmount}</p>
              <p><strong>Status:</strong> <span className="text-red-600 font-semibold">Failed / Expired</span></p>
            </div>

            <button
              onClick={() => { setPaymentData(null); setPaymentStatus('pending'); setError(''); }}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full mt-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg transition"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Payment tracking state
  if (paymentData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <nav className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-bold text-blue-600">PaySolutions</h1>
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-800"
            >
              Back
            </button>
          </div>
        </nav>

        <div className="max-w-md mx-auto py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Scan to Pay</h2>

            {/* Tracking Status */}
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-3 mb-4">
                <div className="animate-spin">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-gray-800">We are tracking your payment</p>
                  <p className="text-sm text-gray-600">If you have already paid, do not pay again</p>
                </div>
              </div>
              {paymentData?.merchantOrderNo && (
                <div className="bg-white p-3 rounded border border-blue-300">
                  <p className="text-xs text-gray-600">📌 Order Number for Reference:</p>
                  <p className="text-sm font-mono font-bold text-blue-700 mt-1">{paymentData.merchantOrderNo}</p>
                  <p className="text-xs text-gray-500 mt-2">Please note and save this order number if anything goes wrong with your payment.</p>
                </div>
              )}
            </div>

            <div className="mb-6 text-center">
              <p className="text-sm font-semibold text-gray-800 mb-2">Complete Your Payment</p>
              <p className="text-xs text-amber-600 mb-4">⏱️ Please complete your payment before the timer on the page expires</p>
              <iframe
                src={paymentData.paymentUrl}
                title="Payment Page"
                className="w-full rounded-lg border-2 border-gray-300"
                style={{ height: '600px' }}
                allow="payment"
              />
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
              <p className="text-sm font-semibold text-blue-900">
                Your wallet will receive {paymentData.walletWillReceive} INR
              </p>
              <p className="text-xs text-blue-700 mt-1">
                ({paymentData.commission} INR deducted as commission)
              </p>
            </div>

            <div className="space-y-2 mb-6">
              <p className="text-sm text-gray-600"><strong>Amount:</strong> ₹{paymentData.grossAmount}</p>
              <p className="text-sm text-gray-600"><strong>Commission:</strong> ₹{paymentData.commission}</p>
              <p className="text-sm text-gray-600"><strong>You'll Get:</strong> ₹{paymentData.walletWillReceive}</p>
            </div>

            <a
              href={paymentData.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full block text-center bg-blue-500 hover:bg-blue-600 text-white font-semibold py-3 px-4 rounded-lg transition"
            >
              Open in new tab ↗
            </a>

            <p className="text-center text-xs text-gray-500 mt-4">
              Status: <span className="text-green-600 font-semibold">Tracking Active ⏳</span>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800">
      <nav className="bg-slate-800 border-b border-slate-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded flex items-center justify-center text-lg font-bold">₹</div>
            <h1 className="text-2xl font-bold text-white">PaySolutions</h1>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="text-gray-400 hover:text-yellow-400 transition"
          >
            Back
          </button>
        </div>
      </nav>

      <div className="max-w-md mx-auto py-12 px-4">
        <div className="bg-slate-800 rounded-xl shadow-lg p-8 border border-slate-700">
          <h2 className="text-2xl font-bold text-white mb-6">💳 Add Money to Wallet</h2>

          {error && (
            <div className="bg-red-900/20 border border-red-500/50 text-red-400 px-4 py-3 rounded-lg mb-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleAddMoney} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Enter Amount (INR)</label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                min="1"
                step="0.01"
                className="w-full px-4 py-3 bg-slate-700/50 border border-slate-600 rounded-lg focus:outline-none focus:border-yellow-400 focus:ring-2 focus:ring-yellow-400/20 text-white placeholder-gray-500 transition"
                placeholder="100.00"
              />
            </div>

            {amount && (
              <div className="bg-gradient-to-r from-blue-600/20 to-blue-700/20 border border-blue-500/50 p-4 rounded-lg">
                <p className="text-sm text-gray-300">
                  <strong>Commission ({commissionRate}%):</strong> <span className="text-yellow-400 font-semibold">₹{(parseFloat(amount) * commissionRate / 100).toFixed(2)}</span>
                </p>
                <p className="text-sm font-semibold text-emerald-400 mt-2">
                  <strong>Your Wallet Will Receive:</strong> ₹{(parseFloat(amount) * (100 - commissionRate) / 100).toFixed(2)}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-slate-900 font-bold py-3 px-4 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-yellow-400/50"
            >
              {loading ? 'Processing...' : 'Continue to Payment'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
